/** Schedule → Departures: **Create departure tag now** (plan D8 — an explicit "… now"
 *  action, not a hidden side effect of editing). The operator types a time; the tag of the
 *  operator's "departure" category is created (or found) on the server at once — audited
 *  there — and then added to the draft's departures, one undo step. Saving the tour is
 *  what attaches it; undoing leaves the tag in the operator's catalog.
 *
 *  Edit mode only; its place in the group's heading row is reserved in both modes. */
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Button, Popover, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as toursApi from '@/api/tours';
import { ImmediateActionButton } from '@/features/records/actions/ImmediateActionButton';
import { useDraft } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';

import { DEPARTURES_PATH, TOUR_ENTITY } from '../catalog';
import { useTourRecord } from '../TourRecordContext';

const TIME_RE = /^([01]?\d|2[0-3]):[0-5]\d$/;

export function CreateDepartureNow() {
  const { detail } = useTourRecord();
  const { mode, rememberOptions, config } = useRecordEditor();
  const { state, dispatch, baselineOf } = useDraft();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [time, setTime] = useState('09:00');
  const rec = `${TOUR_ENTITY}:${detail.id}`;
  const field = config.fields.find((f) => f.key === DEPARTURES_PATH);
  const editable = field ? (config.editabilityOf?.(rec, field)?.editable ?? true) : false;

  const create = useMutation({
    mutationFn: (t: string) => toursApi.createDepartureTag(detail.tenant!.id, t),
    onSuccess: (tag) => {
      rememberOptions(DEPARTURES_PATH, [{ id: tag.id, label: tag.name, group: 'Departures' }]);
      const entry = state.entries[rec]?.[DEPARTURES_PATH];
      const current = (entry ? entry.to : baselineOf(rec, DEPARTURES_PATH)) as number[] | undefined;
      const list = Array.isArray(current) ? current : [];
      if (!list.includes(tag.id)) {
        dispatch({
          type: 'SET',
          changes: [{ rec, path: DEPARTURES_PATH, from: baselineOf(rec, DEPARTURES_PATH), to: [...list, tag.id].sort((a, b) => a - b) }],
          label: `Add the ${tag.name} departure`,
        });
      }
      setAnchor(null);
    },
  });

  if (mode !== 'edit' || !detail.tenant || !editable) return null;
  const valid = TIME_RE.test(time.trim());
  const error = create.error ? (create.error instanceof ConsoleApiError ? create.error.message : 'Could not create the departure tag') : null;

  return (
    <>
      <ImmediateActionButton onClick={(e) => setAnchor(e.currentTarget)} sx={{ flex: 'none' }}>
        Create departure tag now
      </ImmediateActionButton>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 1.5, width: 320, maxWidth: '90vw' } } }}
      >
        <Box
          component="form"
          sx={{ display: 'grid', gap: 1 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (valid && !create.isPending) create.mutate(time.trim());
          }}
        >
          <Typography variant="body2">
            Creates the departure tag in the operator’s “departure” category <b>at once</b> (or finds it) — that is
            not part of the draft and stays if you discard. The tag is then added to this tour’s departures in the
            draft; saving the tour attaches it.
          </Typography>
          <TextField
            type="time"
            size="small"
            label="Departure time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 60 }}
            error={!valid}
            helperText={valid ? ' ' : 'Enter a time as HH:MM'}
            autoFocus
          />
          {error && (
            <Typography variant="caption" color="error" sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} />
              {error}
            </Typography>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button size="small" onClick={() => setAnchor(null)}>
              Cancel
            </Button>
            <Button size="small" variant="contained" type="submit" disabled={!valid || create.isPending}>
              {create.isPending ? 'Creating…' : 'Create and add'}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
